/*
 * @Description: 实时定位驾驶舱 — C1「事件流」数据 hook（支持分页加载更多）
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { fetchEventsFeed } from '../../../actions/locationCockpit'
import { REALTIME_POLL_INTERVAL_MS, isCockpitMockEnabled } from '../constants'
import { MOCK_EVENTS_FEED } from '../mock/events.mock'
import {
  CockpitEnvelope,
  CockpitEventItem,
  EventsFeedData
} from '../types/cockpit.types'

const PAGE_SIZE = 20

export interface UseEventsFeedResult {
  events: CockpitEventItem[]
  loading: boolean
  /** 是否还有下一页 */
  hasMore: boolean
  /** 加载下一页（滑到底部时调用） */
  loadMore: () => void
  /** 手动刷新一次（回到首页） */
  refresh: () => void
}

/**
 * 拉取实时定位驾驶舱 C1「事件流」（右侧实时动态卡）。
 * 按日期 + 事件类型筛选；支持滚动分页加载更多；实时态（当天）按 60s 轮询刷新首页。
 * mock 模式直接返回本地 fixtures（单页）。
 *
 * @param groupId - 当前团队长 id
 * @param date - 查询日期 YYYY-MM-DD
 * @param type - 事件类型筛选（'all' = 全部事件）
 * @param isToday - date 是否为当天（决定是否轮询）
 */
export function useEventsFeed(
  groupId: string,
  date: string,
  type: string,
  isToday: boolean,
  pollIntervalMs: number = REALTIME_POLL_INTERVAL_MS
): UseEventsFeedResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [events, setEvents] = useState<CockpitEventItem[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const aliveRef = useRef(true)
  const loadingRef = useRef(false)
  const pageRef = useRef(1)
  // 累计已加载事件，避免分页 setState 闭包过期
  const accRef = useRef<CockpitEventItem[]>([])

  const fetchPage = useCallback(
    (pageNo: number, append: boolean) => {
      if (isMock) {
        const all = MOCK_EVENTS_FEED.items
        const filtered =
          type === 'all' ? all : all.filter((e) => e.type === type)
        accRef.current = filtered
        pageRef.current = 1
        setEvents(filtered)
        setHasMore(false)
        return
      }
      if (!groupId) return

      loadingRef.current = true
      setLoading(true)
      dispatch(
        fetchEventsFeed({ groupId, date, type, pageNo, pageSize: PAGE_SIZE })
      )
        .then((res: CockpitEnvelope<EventsFeedData>) => {
          if (!aliveRef.current) return
          loadingRef.current = false
          setLoading(false)
          if (res && res.code === 200) {
            const items = res.data?.items ?? []
            const total = res.data?.total ?? 0
            pageRef.current = pageNo
            const next = append ? accRef.current.concat(items) : items
            accRef.current = next
            setEvents(next)
            setHasMore(next.length < total)
          }
        })
        .catch(() => {
          if (!aliveRef.current) return
          loadingRef.current = false
          setLoading(false)
        })
    },
    [dispatch, groupId, date, type, isMock]
  )

  /** 刷新到第一页（筛选变化 / 轮询用） */
  const refresh = useCallback(() => {
    pageRef.current = 1
    fetchPage(1, false)
  }, [fetchPage])

  /** 加载下一页（滑到底部用） */
  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMore) return
    fetchPage(pageRef.current + 1, true)
  }, [fetchPage, hasMore])

  useEffect(() => {
    aliveRef.current = true
    refresh()

    // mock 与历史态不轮询；实时态仅在停留首页时轮询刷新，避免打断已加载的分页
    if (isMock || !isToday) {
      return () => {
        aliveRef.current = false
      }
    }
    const timer = window.setInterval(() => {
      if (pageRef.current === 1) refresh()
    }, pollIntervalMs)
    return () => {
      aliveRef.current = false
      window.clearInterval(timer)
    }
  }, [refresh, isMock, isToday, pollIntervalMs])

  return { events, loading, hasMore, loadMore, refresh }
}
